import { apiError } from "../../../../../../utils/formErrors";
import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, Stack, TextField, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import BtnPrimary from '../../../../../../component/PrimaryButton';
import BtnSecondary from '../../../../../../component/SecondaryButton';
import FormField from '../../../../../../component/Formfield';
import SectionLoader from '../../../../../../component/SectionLoader';
import { FormSection, FieldGrid, FieldLabel, SwitchRow, WizardActions } from '../../../../../../component/wizard';
import { addSoftware, updateSoftware, getAssetById, getVendors } from '../../../../../../services/asset';
import { useNotify } from '../../../../../../component/NotificationProvider';
import { tokens } from '../../../../../../theme';
import RecordField from '../../../../../../component/RecordField';
import AssetTransferHistory from '../AssetTransferHistory';
import { getLegalUnitList } from '../../../../../../services/globalData';
import { StatusChip } from '../../../../../../component/listing';

import { displayFont } from "../../../../../../theme";
const dateInputSx = {
  width: "100%", boxSizing: "border-box", fontFamily: "inherit", fontSize: "0.875rem", color: tokens.text,
  border: `1px solid ${tokens.borderStrong}`, borderRadius: "8px", padding: "11px 14px",
  background: tokens.card, outline: "none", "&:hover": { borderColor: tokens.textMuted }, "&:focus": { borderColor: tokens.primary, boxShadow: `inset 0 0 0 1px ${tokens.primary}` },
};
const toInput = (d) => (d ? new Date(d).toISOString().split("T")[0] : "");

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
/** "14 September 2026" — spelled from a table; ICU renders "Sept" for September. */
const longDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  if (!m) return "";
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
};

const SoftwareForm = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { state } = useLocation();
  const [mode, setMode] = useState(state?.mode === "view" ? "view" : "create");
  const [assetId] = useState(state?.assetId || null);
  const [loading, setLoading] = useState(Boolean(state?.assetId));
  const [apiCall, setApiCall] = useState(false);
  const [form, setForm] = useState({
    name: "", manufacturer: "", type: "", version: "", status: "active", vendor: "",
    licensed: false, licenseKey: "", licenseSeats: "", purchasedOn: "", expiredOn: "",
  });
  const [errors, setErrors] = useState({});
  // active only: a retired supplier keeps its link on existing assets but should
  // not be offered for a new one
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    getVendors(true)
      .then((res) => setVendors(Array.isArray(res?.data) ? res.data : []))
      .catch(() => {}); // the rest of the form still works without the list
  }, []);
  const readOnly = mode === "view";
  // Which company's books this asset sits on. Read from the cached entity list
  // rather than populated by the API, which keeps the asset payload unchanged.
  const entityName =
    (getLegalUnitList() || []).find((u) => String(u._id) === String(form.legalUnit))?.name ||
    "No entity yet";

  useEffect(() => {
    if (!state?.assetId) return;
    let active = true;
    (async () => {
      try {
        const res = await getAssetById(state.assetId);
        if (res?.success === false) throw apiError(res, "Failed to load software");
        const a = res?.data || {};
        if (!active) return;
        setForm({
          name: a.name || "", manufacturer: a.manufacturer || "", type: a.type || "", version: a.version || "",
          status: a.status || "active", vendor: a.vendor?._id || a.vendor || "",
          legalUnit: a.legalUnit?._id || a.legalUnit || "",
          licensed: Boolean(a.licensed), licenseKey: a.licenseKey || "",
          licenseSeats: a.licenseSeats ?? "", purchasedOn: toInput(a.purchasedOn), expiredOn: toInput(a.expiredOn),
        });
      } catch (e) {
        notify.error(e?.message || "Failed to load software");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: "" }));
  };

  const submit = async () => {
    const e = {};
    if (!form.name) e.name = "Name is required";
    if (!form.type) e.type = "Type is required";
    if (Object.keys(e).length) { setErrors(e); return; }

    const payload = {
      name: form.name, manufacturer: form.manufacturer, type: form.type, version: form.version,
      status: form.status, licensed: form.licensed,
      ...(form.licensed ? {
        licenseKey: form.licenseKey,
        licenseSeats: form.licenseSeats === "" ? null : Number(form.licenseSeats),
        purchasedOn: form.purchasedOn || null,
        expiredOn: form.expiredOn || null,
      } : {}),
    };

    setApiCall(true);
    try {
      const res = assetId ? await updateSoftware(assetId, payload) : await addSoftware(payload);
      if (res?.success === false) throw apiError(res, "Could not save software");
      notify.success(assetId ? "Software updated" : "Software added");
      navigate("/app/software");
    } catch (err) {
      notify.error(err?.message || "Something went wrong saving the software");
    } finally {
      setApiCall(false);
    }
  };

  const title = mode === "create" ? "Add Software" : mode === "edit" ? "Edit Software" : form.name || "Software";

  return (
    /*
      No TopBar and no WizardCard here: the parent route already draws the page
      header and the card this sits in. Rendering both again gave the screen two
      headings and three nested card grounds.
    */
    <Box sx={{ width: "100%" }}>
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600 }}>{title}</Box>
          <Stack direction="row" spacing={1}>
            <BtnSecondary to="/app/software" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
            {readOnly && <BtnPrimary onClick={() => setMode("edit")} sx={{ borderRadius: "8px" }}>Edit</BtnPrimary>}
          </Stack>
        </Stack>

        {loading ? <SectionLoader /> : (
          <>
            {/*
              A record being read is label and value, not a disabled form. The
              edit controls are the same fields with `readOnly` off, so there is
              one definition of what a software asset holds.
            */}
            {readOnly ? (
              <>
                <FormSection title="Software details">
                  <FieldGrid>
                    <RecordField label="Name" value={form.name} />
                    <RecordField label="Type" value={form.type} />
                    <RecordField label="Manufacturer" value={form.manufacturer} />
                    <RecordField label="Version" value={form.version} />
                    <RecordField label="Status">
                      {/* The stored value is an enum; a reader wants the badge
                          they see everywhere else, not the raw word. */}
                      <StatusChip
                        label={form.status === "inactive" ? "Retired" : "Active"}
                        tone={form.status === "inactive" ? "muted" : "success"}
                      />
                    </RecordField>
                    <RecordField
                      label="Vendor"
                      value={vendors.find((v) => v._id === form.vendor)?.name}
                    />
                  </FieldGrid>
                </FormSection>

                <FormSection title="Licensing">
                  {form.licensed ? (
                    <FieldGrid>
                      <RecordField label="License key" field="licenseKey" value={form.licenseKey} />
                      <RecordField label="Seats" value={form.licenseSeats} />
                      <RecordField label="Purchased on" value={longDate(form.purchasedOn)} />
                      <RecordField label="Expires on" value={longDate(form.expiredOn)} />
                    </FieldGrid>
                  ) : (
                    <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>
                      Not licensed — no key, seats or validity are tracked for this software.
                    </Box>
                  )}
                </FormSection>

                {/* Where this asset sits on the books, and how it got there.
                    Shown on the record rather than only inside the move dialog:
                    somebody asking "why is this on the other company's books?"
                    is looking at the asset, not about to move it. */}
                <FormSection
                  title="Ownership"
                  description="The legal entity that owns this asset, and every time it changed."
                >
                  <FieldGrid>
                    <RecordField label="Owned by" value={entityName} />
                  </FieldGrid>
                  <Box sx={{ mt: "1.25rem" }}>
                    <AssetTransferHistory assetId={state?.assetId} />
                  </Box>
                </FormSection>
              </>
            ) : (
              <>
            <FormSection title="Software details">
              <FieldGrid>
                <Box>
                  <FieldLabel required>Name</FieldLabel>
                  <FormField type="text" value={form.name} validation={errors.name} disabled={readOnly} onChange={(e) => change('name', e.target.value)} />
                </Box>
                <Box>
                  <FieldLabel required>Type</FieldLabel>
                  <FormField type="select" options={["Productivity", "Graphics", "Support", "Accounting", "Development"]} value={form.type} validation={errors.type} disabled={readOnly} onChange={(e) => change('type', e.target.value)} />
                </Box>
                <Box>
                  <FieldLabel>Manufacturer</FieldLabel>
                  <FormField type="text" value={form.manufacturer} disabled={readOnly} onChange={(e) => change('manufacturer', e.target.value)} />
                </Box>
                <Box>
                  <FieldLabel>Version</FieldLabel>
                  <FormField type="text" value={form.version} disabled={readOnly} onChange={(e) => change('version', e.target.value)} />
                </Box>
                <Box>
                  <FieldLabel>Status</FieldLabel>
                  <FormField type="select" options={["active", "inactive"]} value={form.status} disabled={readOnly} onChange={(e) => change('status', e.target.value)} />
                </Box>
                <Box>
                  {/* the API has always accepted a vendor on software assets;
                      only the machine form ever offered one, so software could
                      not record where it was bought */}
                  <FieldLabel>Vendor</FieldLabel>
                  <Autocomplete
                    size="small"
                    disabled={readOnly}
                    options={vendors}
                    getOptionLabel={(o) => o?.name || ""}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    value={vendors.find((v) => v._id === form.vendor) || null}
                    onChange={(e, v) => change('vendor', v?._id || "")}
                    renderInput={(params) => <TextField {...params} placeholder="Where it was bought" />}
                  />
                </Box>
              </FieldGrid>
            </FormSection>

            <FormSection title="Licensing">
              <SwitchRow title="Licensed software" description="Track license key, seats and validity."
                checked={form.licensed} disabled={readOnly} onChange={(e, checked) => change('licensed', checked)} />
              {form.licensed && (
                <FieldGrid>
                  <Box>
                    <FieldLabel>License Key</FieldLabel>
                    <FormField type="text" value={form.licenseKey} disabled={readOnly} onChange={(e) => change('licenseKey', e.target.value)} />
                  </Box>
                  <Box>
                    <FieldLabel>Number of Seats</FieldLabel>
                    <FormField type="number" value={form.licenseSeats} disabled={readOnly} onChange={(e) => change('licenseSeats', e.target.value)} />
                  </Box>
                  <Box>
                    <FieldLabel>Purchased On</FieldLabel>
                    <Box component="input" type="date" value={form.purchasedOn} disabled={readOnly} onChange={(e) => change('purchasedOn', e.target.value)} sx={dateInputSx} />
                  </Box>
                  <Box>
                    <FieldLabel>Expires On</FieldLabel>
                    <Box component="input" type="date" value={form.expiredOn} disabled={readOnly} onChange={(e) => change('expiredOn', e.target.value)} sx={dateInputSx} />
                  </Box>
                </FieldGrid>
              )}
            </FormSection>

              </>
            )}

            {!readOnly && (
              <WizardActions>
                <BtnSecondary to="/app/software" sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
                <BtnPrimary onClick={submit} disabled={apiCall} sx={{ borderRadius: "8px" }}>
                  {apiCall ? <CircularProgress size={"1.25rem"} /> : assetId ? "Save Changes" : "Add Software"}
                </BtnPrimary>
              </WizardActions>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default SoftwareForm;
